/* FIAB — Text Overlay
   Delegated open/close for the PDP text overlays. One panel per section
   instance, matched to its trigger by key:
     <a data-overlay-open="ingredients">  ->  .fiab-overlay[data-overlay-panel="ingredients"]
   Loaded once however many panels are on the page.

   The sheet slides in from the right edge. `is-open` drives the entrance;
   closing swaps to `is-closing` and only sets `hidden` once the exit has
   played out, so the panel does not vanish mid-transition. */
(function () {
  if (window.fiabTextOverlay) return;
  window.fiabTextOverlay = true;

  var DURATION = 520;   /* keep in step with --fiab-overlay-duration in the CSS */
  var lastFocused = null;
  var lockedScrollY = 0;

  function motionOff() {
    return !!(window.matchMedia &&
              window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function clearPending(panel) {
    if (panel.fiabTimer) {
      clearTimeout(panel.fiabTimer);
      panel.fiabTimer = null;
    }
  }

  /* ---- The visual viewport --------------------------------------------------
     `position: fixed` is placed against the *layout* viewport, and the layout
     viewport is not always the part of the page the reader can see. Pinch-zoom,
     an on-screen keyboard and a device frame taller than the window all leave a
     visual viewport that is smaller than the layout one and scrolled somewhere
     inside it. Measured on the store, mid-page:

         innerHeight             2669    the layout viewport
         visualViewport.height    667    what is actually on screen
         visualViewport.offsetTop 2002   how far down the layout viewport it sits

     So `inset: 0` put the sheet exactly where it was asked to go — two thousand
     pixels above the reader, who then had to scroll to the top of the page to
     find it. Nothing was trapping it and nothing needed correcting after the
     fact; it was being measured against the wrong box.

     visualViewport reports that box directly. Hand it to the CSS and the sheet
     sits on the part of the page being looked at. Where the two viewports agree
     — which is every ordinary phone, unzoomed — the numbers are 0 / full height
     and nothing moves. Engines without the API fall through to the viewport
     units in the fallback half of each var(). */
  function syncViewport() {
    var vv = window.visualViewport;
    if (!vv) return;
    var doc = document.documentElement;
    doc.style.setProperty('--fiab-vv-left', vv.offsetLeft + 'px');
    doc.style.setProperty('--fiab-vv-top', vv.offsetTop + 'px');
    doc.style.setProperty('--fiab-vv-width', vv.width + 'px');
    doc.style.setProperty('--fiab-vv-height', vv.height + 'px');
  }

  function clearViewport() {
    var doc = document.documentElement;
    doc.style.removeProperty('--fiab-vv-left');
    doc.style.removeProperty('--fiab-vv-top');
    doc.style.removeProperty('--fiab-vv-width');
    doc.style.removeProperty('--fiab-vv-height');
  }

  /* Bound only while a sheet is up. The visual viewport moves under zoom and
     under the keyboard, and its own events are the only ones that report it —
     a window scroll listener does not see it move. */
  function watchViewport(on) {
    var vv = window.visualViewport;
    if (!vv) return;
    var fn = on ? 'addEventListener' : 'removeEventListener';
    vv[fn]('resize', syncViewport);
    vv[fn]('scroll', syncViewport);
  }

  /* ---- Page lock ------------------------------------------------------------
     Held on <html>, with the width the scrollbar gave up handed to the CSS as a
     gutter. Locking used to drop the scrollbar outright, so the page behind
     relaid out on the same frame the sheet began to slide; it was only given
     back after the exit had finished, which is why closing looked settled and
     opening did not.

     The offset is remembered and put back on unlock. Making the scrollport
     non-scrollable clamps it to zero in some engines, so without this the page
     behind quietly rewinds to the top while the sheet covers it, and the reader
     lands somewhere else entirely once it closes. */
  function lockPage() {
    var doc = document.documentElement;
    if (doc.classList.contains('fiab-overlay-locked')) return;
    lockedScrollY = window.pageYOffset || doc.scrollTop || 0;
    var gutter = window.innerWidth - doc.clientWidth;
    doc.style.setProperty('--fiab-overlay-gutter', (gutter > 0 ? gutter : 0) + 'px');
    doc.classList.add('fiab-overlay-locked');
  }

  function unlockPage() {
    var doc = document.documentElement;
    if (!doc.classList.contains('fiab-overlay-locked')) return;
    doc.classList.remove('fiab-overlay-locked');
    doc.style.removeProperty('--fiab-overlay-gutter');

    if ((window.pageYOffset || doc.scrollTop || 0) === lockedScrollY) return;
    /* base.css puts `scroll-behavior: smooth` on the root — restoring the
       offset has to be instant, or the page animates back under the reader. */
    var behavior = doc.style.scrollBehavior;
    doc.style.scrollBehavior = 'auto';
    window.scrollTo(0, lockedScrollY);
    doc.style.scrollBehavior = behavior;
  }

  function open(panel) {
    var trigger = document.activeElement;

    closeAll(panel);
    clearPending(panel);
    panel.classList.remove('is-closing');

    /* A trigger reached by touch may never take focus, which would leave the
       page's own <body> to be "restored" to on close. */
    lastFocused = (trigger && trigger !== document.body) ? trigger : null;
    panel.hidden = false;
    lockPage();
    syncViewport();
    watchViewport(true);

    var scroll = panel.querySelector('.fiab-overlay__scroll');
    if (scroll) scroll.scrollTop = 0;

    /* Focus while the sheet is still parked, and keep the browser from
       scrolling to it. The overlay clips its overflow, which makes it a scroll
       container all the same, so revealing a control that sits a panel-width
       offscreen sets its scrollLeft — and base.css puts `scroll-behavior:
       smooth` on the root, so that correction animated. The sheet then slid in
       across a container drifting back to zero underneath it, which is the jump
       and the bounce. Closing focuses the trigger, which is on screen already,
       so the exit never showed it. Reset either axis in case the option is
       ignored. */
    var close = panel.querySelector('.fiab-overlay__close');
    if (close) close.focus({ preventScroll: true });
    panel.scrollLeft = 0;
    panel.scrollTop = 0;

    /* Flush the offscreen start state before adding the class, otherwise the
       browser coalesces both into one paint and the slide never runs. */
    void panel.offsetWidth;
    panel.classList.add('is-open');
  }

  function close(panel, restoreFocus) {
    clearPending(panel);
    panel.classList.remove('is-open');
    panel.classList.add('is-closing');

    panel.fiabTimer = setTimeout(function () {
      panel.fiabTimer = null;
      panel.hidden = true;
      panel.classList.remove('is-closing');
      /* Only give the page back its scroll if nothing else opened meanwhile */
      if (!document.querySelector('.fiab-overlay.is-open')) {
        unlockPage();
        watchViewport(false);
        clearViewport();
      }
    }, motionOff() ? 0 : DURATION);

    if (restoreFocus && lastFocused) {
      lastFocused.focus({ preventScroll: true });
      lastFocused = null;
    }
  }

  /* `except` is only passed from open(), where focus belongs to the new panel */
  function closeAll(except) {
    var panels = document.querySelectorAll('.fiab-overlay.is-open');
    for (var i = 0; i < panels.length; i++) {
      if (panels[i] !== except) close(panels[i], !except);
    }
  }

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;

    var trigger = e.target.closest('[data-overlay-open]');
    if (trigger) {
      /* Always, even with no panel to show. The triggers are <a href="#desc">
         and friends, and those hashes match nothing on the page — letting one
         through sends the reader to the top of the document (smoothly, since
         base.css sets scroll-behavior on the root) for no visible reason. A
         panel goes missing whenever its section is removed in the editor or
         its key is retyped. */
      e.preventDefault();
      var key = trigger.getAttribute('data-overlay-open');
      var panel = document.querySelector('.fiab-overlay[data-overlay-panel="' + key + '"]');
      if (panel) open(panel);
      return;
    }

    if (e.target.closest('[data-overlay-close]')) closeAll();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeAll();
  });
})();
